import React, { useState } from 'react';

// ─── State config (espeja STATE_COLORS del wallet service) ────────────────────
const STATE_COLORS = {
  'Vigente':              { bg: '#1A3D2E', label: '#4ADE80' },
  'Vigente desde':        { bg: '#0A2A33', label: '#5EEAD4' },
  'En trámite':           { bg: '#12233D', label: '#93C5FD' },
  'Expira pronto':        { bg: '#3D2E0A', label: '#FCD34D' },
  'Renovada':             { bg: '#1A3D2E', label: '#6EE7B7' },
  'Pago pendiente':       { bg: '#3D2200', label: '#FB923C' },
  'Pago rechazado':       { bg: '#3D0A14', label: '#F87171' },
  'Suspendida':           { bg: '#2D0A0A', label: '#EF4444' },
  'Cancelada':            { bg: '#1A1A1A', label: '#9CA3AF' },
  'Siniestro abierto':    { bg: '#0A1A3D', label: '#60A5FA' },
  'Asistencia en camino': { bg: '#0A2233', label: '#38BDF8' },
};

// Claves = valores exactos de producto.ramo
const INSURANCE_LABELS = {
  incendio: 'Seguro Incendio',
  salud:    'Seguro Salud',
  auto:     'Seguro Automotriz',
  hogar:    'Seguro Hogar',
  viaje:    'Seguro Viaje',
  vida:     'Seguro Vida',
  soap:     'SOAP',
  rc:       'Responsabilidad Civil',
};

const ramoLabel = (ramo) =>
  INSURANCE_LABELS[ramo] ?? (ramo ? `Seguro ${ramo.charAt(0).toUpperCase()}${ramo.slice(1)}` : '—');

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtMoney = (amount, currency) => {
  if (amount == null) return '';
  const cur = String(currency || '').toUpperCase();
  if (cur === 'UF') return `${Number(amount).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} UF`;
  if (cur === 'CLP' || cur === '$' || cur === '') return `$${Math.round(Number(amount)).toLocaleString('es-CL')}`;
  return `${Number(amount).toLocaleString('es-CL')} ${cur}`;
};

const FREQ_LABEL = { monthly: 'mensuales', mensual: 'mensuales', weekly: 'semanales', yearly: 'anuales', annual: 'anuales', anual: 'anuales' };
const fmtFreq = (f) => (f ? (FREQ_LABEL[String(f).toLowerCase()] ?? f) : '');

// ─── helpers UI ───────────────────────────────────────────────────────────────

function FieldGroup({ label, value, align = 'left' }) {
  return (
    <div style={{ textAlign: align }}>
      <p style={{ fontSize: 9, letterSpacing: '0.08em', color: '#9CA3AF', marginBottom: 2, textTransform: 'uppercase' }}>
        {label}
      </p>
      <p style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', lineHeight: 1.2 }}>
        {value ?? '—'}
      </p>
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '8px 0' }} />;
}

// ─── resolveDisplayState (espeja el engine del wallet service) ─────────────────

// El "hoy" que decide si una cobertura empezó es el de Chile, no el UTC del
// navegador — mismo criterio que el motor del wallet y que expectedDisplayState
// en tadmin. Si divergen, esta preview muestra un estado distinto al del pase.
const coverageNotStarted = (startDate) => {
  if (!startDate) return false;
  const start = String(startDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return false;
  const hoy = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  return start > hoy;
};

function resolveDisplayState(p) {
  if (!p) return 'Vigente';
  const { coverage_status: c, payment_status: py, claim_status: cl, assistance_status: a } = p;
  if (c === 'suspended')    return 'Suspendida';
  if (c === 'canceled')     return 'Cancelada';
  if (py === 'rejected')    return 'Pago rechazado';
  if (py === 'overdue')     return 'Pago pendiente';
  if (py === 'pending')     return 'Pago pendiente';
  if (coverageNotStarted(p.start_date)) return 'Vigente desde';
  if (c === 'expiring_soon') return 'Expira pronto';
  if (a === 'dispatched' || a === 'arriving') return 'Asistencia en camino';
  if (cl === 'opened' || cl === 'assigned' || cl === 'in_progress') return 'Siniestro abierto';
  if (c === 'renewed') return 'Renovada';
  if (c === 'issuing') return 'En trámite';
  return 'Vigente';
}

// ─── Auxiliary fields (espeja pass-builder.ts) ────────────────────────────────

function buildAuxiliary(policy) {
  if (!policy) return [];
  const fields = [];

  if (policy.insurance_type === 'auto') {
    if (policy.vehicle_plate) fields.push({ label: 'Patente',     value: policy.vehicle_plate });
    if (policy.deductible)    fields.push({ label: 'Deducible',   value: policy.deductible });
    fields.push({ label: 'Asistencia', value: '24/7' });
  } else if (policy.insurance_type === 'hogar' || policy.insurance_type === 'incendio') {
    if (policy.deductible)    fields.push({ label: 'Deducible',   value: policy.deductible });
    fields.push({ label: 'Asistencia', value: 'Hogar 24/7' });
  } else if (policy.insurance_type === 'viaje') {
    fields.push({ label: 'Inicio viaje', value: fmt(policy.start_date) });
    fields.push({ label: 'Emergencias',  value: 'Internacional' });
  } else if (policy.insurance_type === 'salud') {
    if (policy.start_date) fields.push({ label: 'Inicio', value: fmt(policy.start_date) });
    if (policy.subscription_quota_paid !== undefined && policy.subscription_quota_paid !== null && policy.subscription_quota_total) {
      fields.push({ label: 'Cuotas', value: `${policy.subscription_quota_paid}/${policy.subscription_quota_total}` });
    }
    fields.push({ label: 'Red médica', value: 'Ver reverso' });
  }

  if (policy.claim_status && policy.claim_status !== 'none' && policy.claim_status !== 'resolved') {
    fields.push({ label: 'Siniestro', value: `#${(policy.id ?? '').slice(-6).toUpperCase()}` });
  }

  return fields.slice(0, 3);
}

// ─── FRONT ────────────────────────────────────────────────────────────────────

function PassFront({ policy, wallet }) {
  const state     = resolveDisplayState(policy);
  const colors    = STATE_COLORS[state] ?? STATE_COLORS['Vigente'];
  const typeLabel = ramoLabel(policy?.insurance_type);
  const aux       = buildAuxiliary(policy);

  return (
    <div style={{
      width: 320,
      background: '#141414',
      borderRadius: 16,
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      boxShadow: '0 24px 48px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.4)',
    }}>
      {/* accent stripe */}
      <div style={{ height: 4, background: colors.label, opacity: 0.9 }} />

      {/* header: logo + estado */}
      <div style={{ padding: '14px 18px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6,
            background: 'linear-gradient(135deg, #007D6E 0%, #00A896 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Y</span>
          </div>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Yool</span>
        </div>

        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 8, letterSpacing: '0.1em', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 2 }}>
            Cobertura
          </p>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
            color: colors.label, background: colors.bg,
            padding: '2px 8px', borderRadius: 99,
          }}>
            {state}
          </span>
        </div>
      </div>

      <Divider />

      {/* primary: tipo seguro */}
      <div style={{ padding: '8px 18px' }}>
        <p style={{ fontSize: 9, letterSpacing: '0.08em', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 3 }}>Seguro</p>
        <p style={{ fontSize: 17, fontWeight: 700, color: '#FFFFFF', letterSpacing: -0.3, lineHeight: 1.2 }}>
          {typeLabel}
        </p>
        {policy?.insurance_plan && (
          <p style={{ fontSize: 12, color: '#D1D5DB', marginTop: 2 }}>{policy.insurance_plan}</p>
        )}
      </div>

      <Divider />

      {/* secondary: titular + vigencia */}
      <div style={{ padding: '8px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <FieldGroup label="Titular"       value={policy?.holder_name} />
          <FieldGroup label="Vigente hasta" value={fmt(policy?.end_date)} align="right" />
        </div>
      </div>

      {/* auxiliary */}
      {aux.length > 0 && (
        <>
          <Divider />
          <div style={{ padding: '8px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {aux.map((f, i) => <FieldGroup key={i} label={f.label} value={f.value} />)}
            </div>
          </div>
        </>
      )}

      {/* footer: serial + version */}
      <div style={{ padding: '10px 18px 14px' }}>
        <Divider />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontSize: 9, color: '#6B7280', fontFamily: 'monospace' }}>
            {wallet?.serial_number ? `#${wallet.serial_number.slice(-8).toUpperCase()}` : 'sin serial'}
          </span>
          <span style={{ fontSize: 9, color: '#6B7280' }}>v{wallet?.active_version ?? 1}</span>
        </div>
      </div>
    </div>
  );
}

// ─── BACK ─────────────────────────────────────────────────────────────────────

function PassBack({ policy, wallet }) {
  const state  = resolveDisplayState(policy);
  const colors = STATE_COLORS[state] ?? STATE_COLORS['Vigente'];

  const planRows = (() => {
    const total = policy?.subscription_quota_total;
    if (!total) return [];
    const money = fmtMoney(policy?.subscription_quota_amount, policy?.subscription_quota_currency);
    const plan = [`${total} cuotas`, fmtFreq(policy?.subscription_quota_frequency), money && `de ${money}`].filter(Boolean).join(' ');
    const paid = policy?.subscription_quota_paid ?? 0;
    const pending = policy?.subscription_quota_pending ?? 0;
    const failed = policy?.subscription_quota_failed ?? 0;
    const estado = [`${paid} pagadas`, `${pending} pendientes`];
    if (failed > 0) estado.push(`${failed} fallidas`);
    if (policy?.subscription_quota_next_date) estado.push(`Próxima: ${fmt(policy.subscription_quota_next_date)}${money ? ` (${money})` : ''}`);
    return [
      { label: 'Plan de pagos', value: plan },
      { label: 'Estado de cuotas', value: estado.join(' · ') },
    ];
  })();

  const rows = [
    { label: 'WhatsApp Yool',  value: '+56 9 XXXX XXXX' },
    { label: 'Soporte',        value: 'hola@yool.cl' },
    { label: 'Portal',         value: 'yooldigital.com' },
    { label: 'N° Certificado', value: policy?.certificate_number },
    { label: 'N° Póliza',      value: policy?.policy_number },
    { label: 'Compañía',       value: policy?.insurance_company },
    { label: 'RUT Titular',    value: policy?.holder_rut },
    ...planRows,
    { label: 'Condiciones',    value: 'Este certificado no reemplaza la póliza original. Visita yooldigital.com para coberturas completas.' },
  ].filter(r => r.value);

  return (
    <div style={{
      width: 320,
      background: '#141414',
      borderRadius: 16,
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      boxShadow: '0 24px 48px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.4)',
    }}>
      <div style={{ height: 4, background: colors.label, opacity: 0.9 }} />
      <div style={{ padding: '14px 18px 10px' }}>
        <span style={{ color: '#9CA3AF', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Reverso del pass
        </span>
      </div>
      <div style={{ padding: '0 18px 18px' }}>
        {rows.map((row, i) => (
          <div key={i}>
            <p style={{ fontSize: 9, letterSpacing: '0.08em', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 2 }}>
              {row.label}
            </p>
            <p style={{ fontSize: 12, color: '#E5E7EB', lineHeight: 1.5, wordBreak: 'break-word', marginBottom: 10 }}>
              {row.value}
            </p>
            {i < rows.length - 1 && <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 10 }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PUBLIC ───────────────────────────────────────────────────────────────────

export default function PassCard({ policy, wallet }) {
  const [showBack, setShowBack] = useState(false);

  if (!policy) {
    return (
      <div style={{
        width: 320, height: 180, background: '#1F2937', borderRadius: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#6B7280', fontSize: 13,
      }}>
        Sin datos de póliza
      </div>
    );
  }

  return (
    <div>
      {showBack
        ? <PassBack policy={policy} wallet={wallet} />
        : <PassFront policy={policy} wallet={wallet} />
      }
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
        <button
          onClick={() => setShowBack(b => !b)}
          style={{
            background: 'none',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#9CA3AF',
            fontSize: 11,
            padding: '4px 16px',
            borderRadius: 99,
            cursor: 'pointer',
            letterSpacing: '0.04em',
          }}
        >
          {showBack ? '← Anverso' : 'Reverso →'}
        </button>
      </div>
    </div>
  );
}
